/**
 * Tipos del dominio compartidos por las rutas.
 * Espejo de los schemas del backend FastAPI (modulos: classify, ledger, masters, filings).
 * Cuando el backend publique sus schemas, este archivo se alinea con ellos.
 */

/* ---------- Identidad, roles y alcance ---------- */

export type Role =
  | "platform_admin"
  | "accountant"
  | "cfo"
  | "client_management"
  | "client_finance"
  | "viewer"
  | "external_auditor"

export type Capability = "read" | "edit" | "approve" | "close_period" | "sign"

export const ROLE_LABELS: Record<Role, string> = {
  platform_admin: "Administrador de plataforma",
  accountant: "Contador",
  cfo: "CFO",
  client_management: "Gerencia del cliente",
  client_finance: "Administracion / Finanzas",
  viewer: "Consulta",
  external_auditor: "Auditor externo",
}

export const CAPABILITY_LABELS: Record<Capability, string> = {
  read: "Leer",
  edit: "Editar",
  approve: "Aprobar",
  close_period: "Cerrar periodo",
  sign: "Firmar",
}

export interface Tenant {
  id: string
  name: string
  ruc: string
  country: string
}

/** El acceso se define en tres ejes: rol x alcance x capacidad. */
export interface Membership {
  tenantId: string
  tenantName: string
  /**
   * TODOS los roles que la persona tiene en este tenant, de mayor a menor
   * alcance. Es una LISTA y no un valor porque el caso real de este producto es
   * la persona que es contador Y CFO de la misma empresa: guardar uno solo la
   * dejaba sin una de las dos secciones. Nunca esta vacia.
   */
  roles: Role[]
  /**
   * El rol de mayor alcance de `roles`. Es lo que se ETIQUETA en pantalla —una
   * credencial no lista siete cargos—, no lo que decide que se ve: eso se
   * decide con `roles` entera. Derivado; no se asigna por separado.
   */
  role: Role
  capabilities: Capability[]
  entityIds?: string[]
  /**
   * Hay al menos una asignacion SIN entidad en este tenant, o sea alcance sobre
   * el tenant ENTERO. No es `entityIds === undefined`: alguien puede tener una
   * asignacion de tenant y otra acotada a una sociedad, y entonces `entityIds`
   * viene llena y el alcance sigue siendo el tenant. Se guarda aparte porque
   * decide que secciones tiene sentido mostrar (`lib/secciones.ts`).
   */
  tenantWide: boolean
  /**
   * ISO-3166 alpha-2 del pais de la SOCIEDAD (PE, CL, BR, ES...).
   * Gobierna idioma, formato numerico y terminologia fiscal de la pantalla:
   * el idioma sigue a la entidad, no al navegador. Si falta, se asume PE.
   */
  country?: string
}

export interface UserProfile {
  uid: string
  email: string
  displayName: string
  /** Alcance global (admin de plataforma). */
  global: boolean
  memberships: Membership[]
  /**
   * Roles que no cuelgan de ningun cliente —hoy solo el admin de plataforma—.
   * Van aparte de `memberships` a proposito: una membresia responde «que puedo
   * hacer EN este cliente», y un alcance sin cliente no cabe en esa pregunta.
   * Metidos ahi dentro nacia una membresia con `tenantId: null` que dejaba la
   * entrada del producto girando para siempre. Ver `authMapping.ts`.
   */
  globalRoles: Role[]
  /**
   * El idioma de interfaz que eligio ESTA persona, o `null` si no eligio.
   *
   * `null` NO es «español»: es «sigue al pais de la sociedad». Ver
   * `lib/i18n.tsx`. La terminologia fiscal, la moneda y el formato de numero NO
   * dependen de esto: van por la sociedad, porque el ICMS es ICMS lo mire quien
   * lo mire.
   */
  idioma: "es" | "pt" | "en" | null
}

/* ---------- Libros ---------- */

export type BookId = "STATUTORY_PE" | "TAX" | "MANAGEMENT"

/**
 * LOS TRES LIBROS, nombrados por A QUIÉN RESPONDE CADA UNO.
 *
 * Cesar: «¿qué es statutory (PE)? No me queda claro eso vs tributario y
 * gerencial en tus filtros de libro». El rótulo tenía dos defectos y los dos
 * eran de nombre, no de concepto:
 *
 *   ESTABA EN INGLÉS entre dos palabras en español. «Statutory, Tributario,
 *   Gerencial» no se lee como una serie de tres: se lee como dos cosas y una
 *   rara, y la rara es justo la que hay que entender primero porque es el libro
 *   legal.
 *
 *   Y LLEVABA «(PE)» PEGADO, que es falso desde que hay más de un país. El `_PE`
 *   viene del identificador antiguo y el propio test de `routes/books/mapping`
 *   lo dice con todas sus letras: «el país vive en la ENTIDAD, no en el tipo».
 *   La sociedad brasileña mostraba su libro legal rotulado «(PE)».
 *
 * Ahora los tres nombran a su destinatario, que es lo que de verdad los
 * distingue: el SOCIETARIO es el que ven los socios y el regulador bajo la norma
 * contable local; el TRIBUTARIO es el mismo con las adiciones y deducciones que
 * quiere el fisco; el GERENCIAL es el que usa la empresa para decidir.
 */
export const BOOK_LABELS: Record<BookId, string> = {
  STATUTORY_PE: "Societario",
  TAX: "Tributario",
  MANAGEMENT: "Gerencial",
}

/* ---------- Clasificacion / cola de revision ---------- */

export type ClassificationSource = "regla" | "memoria" | "llm" | "humano"

export type ReviewStatus = "pendiente" | "aprobado" | "corregido"

export const NATURALEZAS = ["ingreso", "costo", "gasto", "activo", "pasivo", "patrimonio"] as const
export type Naturaleza = (typeof NATURALEZAS)[number]

/** Cómo se llama cada naturaleza en la pantalla. «Venta», no «ingreso»: en Finanzas se habla de ventas. */
export const NATURALEZA_LABELS: Record<Naturaleza, string> = {
  ingreso: "Venta",
  costo: "Costo de ventas",
  gasto: "Gasto",
  activo: "Activo",
  pasivo: "Pasivo",
  patrimonio: "Patrimonio",
}

export interface ReviewItem {
  id: string
  /** 01 factura, 03 boleta, 07 NC, 08 ND */
  docType: string
  serie: string
  numero: string
  issueDate: string
  supplierRuc: string
  supplierName: string
  currency: string
  net: number
  igv: number
  total: number
  detractionCode?: string
  detractionRate?: number
  /** Propuesta del motor */
  accountCode: string
  accountName: string
  /**
   * QUÉ ES, por la cuenta de grupo a la que mapea la propuesta: venta, costo,
   * gasto… `null` = la cuenta propuesta no mapea a ninguna cuenta de grupo
   * (un hallazgo: sus importes no entrarían en ningún estado).
   */
  naturaleza: Naturaleza | null
  grupoNombre: string
  /** La línea del P&L de Finanzas a la que va (`account_group.linea_del_modelo`), o vacío. */
  lineaFinanzas: string
  costCenter?: string
  taxTreatment: string
  /**
   * Quien propuso la cuenta, o `null` si NADIE la propuso todavia.
   *
   * El `null` es la mitad importante. La cola trae dos cosas que se parecen y
   * no lo son: un comprobante que el modelo clasifico con poca confianza —hay
   * algo que aprobar o corregir— y un comprobante que NADIE clasifico —no hay
   * nada que aprobar—. El mapeo ponia «llm» por defecto cuando el backend no
   * mandaba fuente, asi que 248 comprobantes sin ninguna clasificacion se
   * pintaban como propuestas de Claude y el contador decia «200 propuestas por
   * el modelo» habiendo cero.
   */
  source: ClassificationSource | null
  /** `null` cuando no hay clasificacion. Cero significaria «el modelo dijo que no sabe». */
  confidence: number | null
  rationale: string
  pipelineRunId: string
  status: ReviewStatus
  ingestChannel: "sunat" | "correo" | "manual" | "banco"
  flags: string[]
  /**
   * Que archivo respalda este comprobante. Lo calcula el BACKEND y no la SPA:
   * dos implementaciones del mismo semaforo acaban discrepando, y el dia que
   * discrepen nadie sabra cual mirar.
   */
  sustento: EstadoSustento
}

/**
 * Que respalda un gasto. Los cuatro estados NO se arreglan igual, y por eso son
 * cuatro y no un booleano:
 *
 *   completo                el comprobante fiscal y su impreso, archivados.
 *   sin_representacion      esta el XML —que es el que vale— y falta archivar
 *                           el PDF. Molesto: hay que ir a buscarlo a Drive.
 *   sin_comprobante_fiscal  hay papel y NO hay documento fiscal. Sin el no hay
 *                           credito fiscal ni, muchas veces, deduccion.
 *   sin_sustento            no hay ningun archivo. Es el peor.
 *
 * Pintar los dos ultimos del mismo color que el segundo haria que se perdieran
 * entre el ruido, y son los que cuestan dinero.
 */
export type EstadoSustento =
  | "completo"
  | "sin_representacion"
  | "sin_comprobante_fiscal"
  | "sin_sustento"

export const SUSTENTO_META: Record<
  EstadoSustento,
  { label: string; tono: "ok" | "aviso" | "alerta"; ayuda: string }
> = {
  completo: {
    label: "Con sustento",
    tono: "ok",
    ayuda: "El comprobante fiscal y su representacion impresa estan archivados.",
  },
  sin_representacion: {
    label: "Falta el PDF",
    tono: "aviso",
    ayuda: "El comprobante vale —esta el XML— pero su impreso no se archivo. "
      + "Habra que ir a buscarlo el dia que lo pidan.",
  },
  sin_comprobante_fiscal: {
    label: "Sin comprobante fiscal",
    tono: "alerta",
    ayuda: "Hay papel del gasto pero no el documento fiscal. Sin el no hay "
      + "credito fiscal ni, en muchos casos, deduccion. Hay que pedirlo.",
  },
  sin_sustento: {
    label: "Sin sustento",
    tono: "alerta",
    ayuda: "No hay ningun archivo que respalde este asiento.",
  },
}

/* ---------- Vista contable ---------- */

export interface JournalLine {
  id: string
  accountCode: string
  accountName: string
  debit: number
  credit: number
  costCenter?: string
  memo?: string
}

export interface JournalEntry {
  id: string
  /** Numero correlativo del asiento */
  number: string
  date: string
  book: BookId
  glosa: string
  documentRef?: string
  origin: "automatico" | "manual" | "ajuste" | "cierre"
  createdBy: string
  createdAt: string
  reason?: string
  status: "borrador" | "posteado" | "reversado"
  lines: JournalLine[]
}

export interface LedgerMovement {
  id: string
  date: string
  entryNumber: string
  glosa: string
  documentRef?: string
  debit: number
  credit: number
  balance: number
  entryId: string
}

export interface LedgerAccount {
  accountCode: string
  accountName: string
  openingBalance: number
  movements: LedgerMovement[]
  closingBalance: number
}

export interface TrialBalanceRow {
  accountCode: string
  accountName: string
  level: number
  openingDebit: number
  openingCredit: number
  periodDebit: number
  periodCredit: number
  closingDebit: number
  closingCredit: number
}

export interface AccountAnalysisRow {
  id: string
  date: string
  entryNumber: string
  entryId: string
  documentRef?: string
  supplier?: string
  glosa: string
  costCenter?: string
  amount: number
  source: ClassificationSource
  sourceDocumentId?: string
}

/* ---------- Maestros ---------- */

export interface Client {
  id: string
  name: string
  ruc: string
  country: string
  currency: string
  paymentTermsDays: number
  /**
   * A DÓNDE SE LE RECLAMA. `party.email_cobranza`.
   *
   * Vive aquí y no en la pantalla de cobranza porque es un dato del cliente, no
   * del envío: antes se tecleaba en cada reclamo y por eso no se recordaba de un
   * mes para otro. Suele ser un buzón —«cuentasxpagar@»— y no una persona. Ver
   * la migración 0024.
   */
  collectionEmail: string
  /**
   * Activar / desactivar (migración 0033). Desactivar no es borrar: la parte
   * conserva sus facturas y su historial; deja de ofrecerse para lo nuevo.
   */
  activo: boolean
  /** Migración 0035: el enlace al contrato firmado y el día del mes en que suele pagar. */
  enlaceContrato: string
  diaDePago: number | null
  /** Migración 0038: en qué línea de la hoja (Revenue) cae. Con esto el desglose abre la línea por cliente. */
  lineaHoja: string
  /** Cuántos contactos tiene (las personas: quién firma, quién aprueba). */
  contactos: number
}

export interface Supplier {
  id: string
  name: string
  ruc: string
  currency: string
  paymentTermsDays: number
  retentionApplies: boolean
  detractionCode?: string
  detractionRate?: number
  /** Ver `Client.activo`. */
  activo: boolean
  /** Migración 0035: ¿se paga todos los meses (prorrateado) o una vez? */
  modalidadGasto: "mensual" | "one_shot" | null
  enlaceContrato: string
  diaDePago: number | null
  /** Migración 0038: en qué línea de la hoja (costo de ventas o gastos) cae. */
  lineaHoja: string
  contactos: number
}

export type BillingModel =
  | "fixed_recurring"
  | "milestone"
  | "time_and_materials"
  | "usage"
  | "one_off"

export const BILLING_MODEL_LABELS: Record<BillingModel, string> = {
  fixed_recurring: "Recurrente fijo (MRR)",
  milestone: "Por hitos",
  time_and_materials: "Tiempo y materiales",
  usage: "Por consumo",
  one_off: "Unico",
}

/**
 * Una linea de contrato. ES DE AQUI DE DONDE SALE EL REVENUE PROYECTADO, no de
 * la caratula: el backend rechaza un contrato sin ninguna linea justamente por
 * eso.
 *
 * `frequency` solo aplica a `fixed_recurring`, y ahi el backend la EXIGE: una
 * linea recurrente sin frecuencia no se puede calendarizar y su ingreso
 * proyectado sale en cero sin que nadie lo note.
 */
export interface ContractLine {
  /** Ausente en una linea nueva: el backend la crea. */
  id?: string
  description: string
  billingModel: BillingModel
  amount: number
  quantity: number
  /** mensual | trimestral | anual | hito | por consumo */
  frequency?: string
  /** Vacio = el contrato entero. El backend la rellena con `vigente_desde`. */
  startDate?: string
  endDate?: string
}

export interface Contract {
  id: string
  clientId: string
  clientName: string
  code: string
  /**
   * El modelo de la PRIMERA linea. Se conserva para la columna del listado y
   * para los contratos de una sola linea, que son la mayoria. Cuando hay
   * varias, `lines` es la fuente: mirar solo esto diria que un contrato mixto
   * es recurrente porque su primera linea lo era.
   */
  billingModel: BillingModel
  currency: string
  indexation: "ninguna" | "ipc" | "usd"
  /** SUMA de `monto x cantidad` de todas las lineas. Derivado, no editable. */
  amount: number
  /**
   * Las lineas del contrato, todas. Mandarlas en un PATCH REEMPLAZA las que
   * haya: el backend borra y reinserta, asi que una edicion parcial que no las
   * incluya no puede tocarlas.
   */
  lines: ContractLine[]
  startDate: string
  endDate: string
  renewal: "automatica" | "manual"
  renewalProbability: number
  paymentTermsDays?: number
  status: "activo" | "vencido" | "borrador"
}

/** El campo mas consecuente del maestro: define el margen bruto. */
export type PersonNature = "costo_venta" | "gasto_operativo" | "capex"

export const PERSON_NATURE_LABELS: Record<PersonNature, string> = {
  costo_venta: "Costo de ventas",
  gasto_operativo: "Gasto operativo",
  capex: "Capex",
}

export type CostBehavior = "fijo" | "variable" | "semi_variable"

export const COST_BEHAVIOR_LABELS: Record<CostBehavior, string> = {
  fijo: "Fijo",
  variable: "Variable",
  semi_variable: "Semi-variable",
}

export type PayrollRegime = "planilla" | "recibo_honorarios"

export const PAYROLL_REGIME_LABELS: Record<PayrollRegime, string> = {
  planilla: "Planilla (5ta categoria)",
  recibo_honorarios: "Recibo por honorarios (4ta)",
}

export interface Person {
  id: string
  /**
   * LA SOCIEDAD A LA QUE PERTENECE. `employee.entity_id`.
   *
   * Existía en la base desde el principio —la clave única es
   * `(entity_id, documento)`— y no llegaba a la SPA: el listado no lo devolvía y
   * el alta lo inyectaba en silencio desde el selector de la barra. Ver
   * `lib/regimenes.ts` para el problema entero.
   */
  entityId: string
  /** Razón social de esa sociedad, ya resuelta por el backend. */
  entityName: string
  /** Su país. Es lo que decide cómo se llama el régimen de esta persona. */
  entityCountry: string | null
  fullName: string
  docNumber: string
  position: string
  costCenterId: string
  costCenterName: string
  nature: PersonNature
  behavior: CostBehavior
  regime: PayrollRegime
  billable: boolean
  clientIds: string[]
  startDate: string
  /** La fecha de salida (`employee.fecha_salida`). Vacío = sigue activa. */
  /** Migración 0038: en qué línea de «Labor Costs» cae. Vacío = la que diga el régimen. */
  lineaHoja: string
  endDate: string
  /** Migración 0035. */
  email: string
  salaryBand: string
  monthlyGross: number
  /** La moneda del sueldo bruto (`employee.moneda`). */
  currency: string
  plannedRaise?: string
  status: "activo" | "baja"
}

export interface CostCenter {
  id: string
  code: string
  name: string
  area: string
  parentId?: string
  defaultNature: PersonNature
  status: "activo" | "inactivo"
}

export interface CollectionPolicy {
  id: string
  name: string
  reminderCadenceDays: number[]
  escalationDay: number
  earlyPaymentDiscountPct: number
  toleranceDaysBeforeSuspension: number
  appliesTo: string[]
}

export interface PaymentPolicy {
  id: string
  name: string
  cutoffDay: number
  termDays: number
  priority: "alta" | "media" | "baja"
  batch: "semanal" | "quincenal" | "mensual"
  appliesTo: string[]
}

/* ---------- Reglas de tratamiento ---------- */

export type TreatmentScopeType = "cuenta" | "categoria" | "proveedor" | "etiqueta" | "condicion"

export type StatutoryAction = "incluir" | "excluir" | "importe_con_limite"
export type TaxAction = "incluir" | "adicion" | "deduccion" | "limite"
export type ManagementAction = "incluir" | "excluir" | "reclasificar" | "normalizar"

export const STATUTORY_ACTION_LABELS: Record<StatutoryAction, string> = {
  incluir: "Incluir",
  excluir: "Excluir",
  importe_con_limite: "Importe con limite",
}

export const TAX_ACTION_LABELS: Record<TaxAction, string> = {
  incluir: "Incluir (deducible)",
  adicion: "Adicion (no deducible)",
  deduccion: "Deduccion",
  limite: "Limite",
}

export const MANAGEMENT_ACTION_LABELS: Record<ManagementAction, string> = {
  incluir: "Incluir",
  excluir: "Excluir del P&L",
  reclasificar: "Reclasificar a otra cuenta",
  normalizar: "Normalizar a un valor",
}

export interface TreatmentRule {
  id: string
  name: string
  scopeType: TreatmentScopeType
  scopeValue: string
  statutory: { action: StatutoryAction; limitPct?: number; limitAmount?: number; note?: string }
  tax: { action: TaxAction; limitPct?: number; limitAmount?: number; legalBasis?: string; note?: string }
  management: {
    action: ManagementAction
    targetAccount?: string
    normalizedValue?: number
    excludeFromEbitda?: boolean
    note?: string
  }
  effectiveFrom: string
  effectiveTo?: string
  author: string
  reason: string
  updatedAt: string
  status: "vigente" | "programada" | "vencida"
}

/* ---------- Consola del contador ---------- */

export type FilingStatus =
  | "en_preparacion"
  | "listo_para_revision"
  | "observado"
  | "firmado"
  | "presentado"

export const FILING_STATUS_LABELS: Record<FilingStatus, string> = {
  en_preparacion: "En preparacion",
  listo_para_revision: "Listo para revision",
  observado: "Observado",
  firmado: "Firmado",
  presentado: "Presentado",
}

export interface FilingException {
  id: string
  severity: "alta" | "media" | "baja"
  title: string
  detail: string
  documentRef?: string
  amount?: number
  source: ClassificationSource
  resolved: boolean
}

export interface FilingDiffRow {
  concept: string
  previous: number
  current: number
  delta: number
  deltaPct: number | null
  note?: string
}

export interface Filing {
  id: string
  tenantId: string
  tenantName: string
  ruc: string
  period: string
  form: string
  dueDate: string
  status: FilingStatus
  preparedBy: string
  signedBy?: string
  igvDebito: number
  igvCredito: number
  igvAPagar: number
  pagoACuenta: number
  exceptionsCount: number
  balanced: boolean
  exceptions: FilingException[]
  diff: FilingDiffRow[]
}

/**
 * Un libro del periodo, tal como lo suma `GET /filings/{id}`
 * (`totales.por_libro`). `balanced` es del LIBRO, no del periodo: el motor
 * lleva tres libros en paralelo y uno puede descuadrar sin arrastrar a los
 * otros. La pantalla necesita saber CUAL para que el contador no tenga que
 * revisar los tres.
 */
export interface FilingBookTotal {
  /** `statutory` | `tax` | `management`, como los devuelve el backend. */
  book: string
  label: string
  debit: number
  credit: number
  entries: number
  balanced: boolean
}

/**
 * El detalle del periodo. NO reemplaza a `Filing`: la bandeja es cross-tenant y
 * trae una fila por periodo; esto es la segunda consulta, por el periodo que se
 * abrio. Lo que la bandeja no puede saber —las excepciones reales, el cuadre por
 * libro, el diff contra el mes anterior— vive aqui y solo aqui.
 *
 * `documentCounts` es el recuento por estado de los comprobantes del periodo. Es
 * el dato que explica una excepcion de "N sin contabilizar" sin abrir otra
 * pantalla.
 */
export interface FilingDetail {
  period: string
  entityName: string
  taxId: string
  country: string
  status: FilingStatus
  preparedBy: string | null
  signedBy: string | null
  bookTotals: FilingBookTotal[]
  documentCounts: { status: string; count: number }[]
  /** Todos los libros cuadran. Un periodo sin asientos no descuadra: ver `hasEntries`. */
  balanced: boolean
  /** Hay al menos un asiento. Distingue "cuadra" de "no hay nada que cuadrar". */
  hasEntries: boolean
  exceptions: FilingException[]
  diff: FilingDiffRow[]
}

/* ---------- Auditoria ---------- */

/** Toda edicion pide motivo. Sin excepcion, en ninguna pantalla. */
export interface EditWithReason<T> {
  payload: T
  reason: string
}
